<template>
  <div>
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
    >
      {{ notification.text }}
    </v-snackbar>
    <v-data-table
      :headers="headers"
      :items="rules"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="pagination"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, -1], showFirstLastPage: true }"
      @pagination="changeRowBackgroundColor"
    >
      <template v-slot:top>
        <div style="float: right">
          <v-btn
            v-if="canAdd"
            id="rule-list-button-new"
            icon
            color="rgba(0, 0, 0, 0.6)"
            class="ma-2 table-button"
            @click="toNewRule()"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            id="rule-list-button-history"
            icon
            color="rgba(0, 0, 0, 0.6)"
            class="ma-2 table-button"
            @click="openAuditLog()"
          >
            <v-icon>mdi-history</v-icon>
          </v-btn>
        </div>
        <div style="clear: both" />
      </template>

      <template v-slot:item.active="{ item }">
        <span v-if="item.active">Active</span>
        <span
          v-else
          class="inactive"
        >Inactive</span>
      </template>
      <template v-slot:item.timeRangeHours="{ item }">
        <span v-if="item.active">{{ item.timeRangeHours }}</span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.transactionCount="{ item }">
        <span v-if="item.active">{{ item.transactionCount }}</span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.includedIdsRanges="{ item }">
        <range-list-cell
          v-if="item.active"
          :range-list="item.includedIdsRanges"
        />
        <span v-else>-</span>
      </template>
      <template v-slot:item.excludedIdsRanges="{ item }">
        <range-list-cell
          v-if="item.active"
          :range-list="item.excludedIdsRanges"
        />
        <span v-else>-</span>
      </template>
      <template v-slot:item.created="{ item }">
        {{ getDateTimeInET(item.created) }}
      </template>
      <template v-slot:item.action="{ item }">
        <v-icon
          v-if="canEdit"
          small
          class="rule-list-row-button-edit mr-2"
          @click="editRule(item.id, item.transactionType)"
        >
          mdi-pencil
        </v-icon>
        <v-icon
          small
          class="rule-list-row-button-history mr-2"
          @click="openAuditLog(item.id, item.transactionType)"
        >
          mdi-history
        </v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { api } from '../http/api'
  import { Routes } from '../router'
  import auth from '../auth/auth'
  import dateUtils from '../util/dateUtils'

  export default {
    name: 'RuleList',

    components: {
      RangeListCell: () => import('../components/RangeListCell'),
    },

    data () {
      return {
        loading: false,
        headers: [
          {
            text: 'Program ID',
            align: 'from',
            value: 'program.code',
            class: 'rule-list-table-header-program',
          },
          {
            text: 'Status',
            value: 'active',
            class: 'rule-list-table-header-active',
          },
          {
            text: 'Transaction Type',
            value: 'transactionType',
            class: 'rule-list-table-header-transactiontype',
          },
          {
            text: 'Hours',
            value: 'timeRangeHours',
            class: 'rule-list-table-header-timerangehours',
          },
          {
            text: 'Transactions',
            value: 'transactionCount',
            class: 'rule-list-table-header-transactioncount',
          },
          {
            text: 'Inclusions',
            sortable: false,
            value: 'includedIdsRanges',
            class: 'rule-list-table-header-inclusions',
          },
          {
            text: 'Exclusions',
            sortable: false,
            value: 'excludedIdsRanges',
            class: 'rule-list-table-header-exclusions',
          },
          {
            text: 'Creation time (ET)',
            value: 'created',
            class: 'rule-list-table-header-created',
          },
          {
            text: 'Actions',
            value: 'action',
            sortable: false,
            class: 'rule-list-table-header-action',
          },
        ],
        rules: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['program.code'],
          sortDesc: [false],
        },
        notification: {
          show: false,
          text: '',
          color: 'success', // ['success', 'info', 'error']
        },
      }
    },

    computed: {
      canEdit: () => auth.canEdit(),
      canAdd: () => auth.canAdd(),
    },

    watch: {
      pagination: {
        deep: true,
        handler () {
          this.fetchRules()
        },
      },
    },

    methods: {
      fetchRules () {
        this.loading = true
        const { page, itemsPerPage, sortBy, sortDesc } = this.pagination
        const sortColumn = Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : 'program.code'
        const sortDirection = Array.isArray(sortDesc) && sortDesc.length > 0 ? sortDesc[0] : false
        // Vuetify uses -1 for the "All" option; translate to a large page size for the server-side query.
        const size = itemsPerPage === -1 ? 100000 : itemsPerPage
        api.getAllRules(page, size, sortColumn, sortDirection).then(value => {
          this.rules = value.rules
          this.totalItems = value.totalRecordsCount
        }).catch(err => {
          this.showErrorMessage(err)
        }).finally(() => {
          this.loading = false
        })
      },
      changeRowBackgroundColor () {
        setTimeout(() => {
          const rows = document.querySelectorAll('table tbody tr')
          rows.forEach(x => {
            const special = x.querySelector('span.inactive')
            if (special) {
              x.setAttribute('style', 'background-color: lightgray')
            }
          })
        }, 200)
      },
      toNewRule () {
        this.$router.push({ name: Routes.NEW_RULE })
      },
      editRule (ruleId, transactionType) {
        this.$router.push({ name: Routes.EDIT_RULE, params: { ruleId: ruleId + '', transactionType: transactionType } })
      },
      openAuditLog (ruleId, transactionType) {
        if (ruleId) {
          this.$router.push({ name: Routes.AUDIT_LOG_RULE, params: { ruleId: ruleId + '', transactionType: transactionType } })
        } else {
          this.$router.push({ name: Routes.AUDIT_LOG })
        }
      },
      getDateTimeInET: (dateString) => {
        return dateUtils.getDateTimeInET(dateString)
      },
      showErrorMessage (err) {
        if (err.data?.message) {
          this.notification = { show: true, text: err.data.message, color: 'error' }
        } else {
          this.notification = { show: true, text: 'Something went wrong.', color: 'error' }
        }
      },
    },
  }
</script>
<style scoped>
  .table-button {
    width: 48px;
    height: 48px;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
</style>
